'use client';
import { useState, useRef, useTransition } from 'react';
import { useTranslations as useTranslations$1, useLocale } from 'next-intl';
import { Mail, ArrowRight } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { signupForWaitlist } from '@/app/[locale]/(marketing)/actions/signup';
import { useRipple } from '@/hooks/useRipple';
import { cn } from '@/lib/utils';
// Email validation regex - requires valid domain with TLD (at least 2 chars)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function validateEmail(email: string): boolean {
    return EMAIL_REGEX.test(email.trim());
}
interface EmailSignupFormProps {
    source?: string;
    buttonText?: string;
    loadingText?: string;
    className?: string;
    onSuccess?: () => void;
}
export function EmailSignupForm({ source = 'homepage', buttonText, loadingText, className = '', onSuccess }: EmailSignupFormProps) {
    const t = useTranslations$1();
    const locale = useLocale();
    const [email, setEmail] = useState('');
    const [isPending, startTransition] = useTransition();
    const [error, setError] = useState<string | null>(null);
    const formRef = useRef<HTMLFormElement>(null);
    const { createRipple, rippleElements } = useRipple();
    // Real-time validation
    const isEmailValid = email.trim() === '' || validateEmail(email);
    const canSubmit = isEmailValid && email.trim() !== '';
    const handleSubmit = (e: React.FormEvent)=>{
        e.preventDefault();
        setError(null);
        // Validate email format before submitting
        if (!validateEmail(email)) {
            setError(t("oLpv29"));
            return;
        }
        startTransition(async ()=>{
            const result = await signupForWaitlist(email, source);
            if (result.success) {
                setEmail('');
                if (onSuccess) {
                    onSuccess();
                } else {
                    // Default: redirect to confirmation page
                    const prefix = locale === 'en' ? '' : `/${locale}`;
                    window.location.href = result.isExisting ? `${prefix}/waitlist/confirmation?existing=true` : `${prefix}/waitlist/confirmation`;
                }
            } else {
                setError(result.error || t("W_5hwr"));
            }
        });
    };
    return (<form ref={formRef} onSubmit={handleSubmit} className={`space-y-4 md:space-y-5 ${className}`}>
      <div className="group relative">
        <Mail className="absolute start-5 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-white/40 transition-all duration-300 group-focus-within:text-white/80"/>
        <Input type="email" placeholder={t("5MDGuM")} value={email} onChange={(e)=>{
        setEmail(e.target.value);
        setError(null); // Clear error when user starts typing
    }} className={cn('h-14 rounded-2xl border ps-14 pe-10 text-base text-white placeholder-white/40 backdrop-blur-xl transition-all duration-300 md:h-16 md:text-lg', email.trim() !== '' && !isEmailValid ? 'border-white/30 bg-white/[0.08] focus:border-white/40 focus:bg-white/10 focus:ring-2 focus:ring-white/20' : 'border-white/15 bg-white/[0.08] hover:bg-white/10 focus:border-white/25 focus:bg-white/10 focus:ring-2 focus:ring-white/10')} required disabled={isPending}/>
        {email && isEmailValid && (<div className="absolute end-4 top-1/2 h-2 w-2 -translate-y-1/2 animate-pulse rounded-full bg-emerald-400"/>)}
      </div>

      {email.trim() !== '' && !isEmailValid && (<p className="text-sm text-white/50" role="alert">
          {t("oLpv29")}
        </p>)}
      {error && (<p className="text-sm text-white/60" role="alert">
          {error}
        </p>)}

      <Button type="submit" onClick={createRipple} disabled={!canSubmit || isPending} className="group relative h-14 w-full cursor-pointer overflow-hidden rounded-2xl border-0 bg-white text-base font-semibold text-black transition-all duration-300 hover:scale-[1.01] hover:bg-white/95 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 md:h-16 md:text-lg">
        <span className="relative z-10 flex items-center justify-center">
          {isPending ? (<>
              <span className="me-3 inline-block h-4 w-4 animate-spin rounded-full border-2 border-black/20 border-t-black"/>
              {loadingText ?? t("eS-NvU")}
            </>) : (<>
              {buttonText ?? t("UyaPHa")}
              <ArrowRight className="ms-3 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"/>
            </>)}
        </span>
        {rippleElements}
      </Button>
    </form>);
}
